import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, X, MapPin, Briefcase, Crown, CheckCircle, Edit, XCircle, Eye, Users, Upload, Trash2 } from 'lucide-react';
import { canCreateGig } from '../utils/accessPolicy';
import { LinkifiedText } from './ui/LinkifiedText';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { FullscreenShell } from './layout/FullscreenShell';

interface GigDetailModalProps {
  gig: any;
  hasInterest: boolean;
  isOwnGig: boolean;
  onClose: () => void;
  onShowInterest: (gigId: string) => void;
  onViewProfile: (userId: string) => void;
  onEdit?: (gigId: string) => void;
  onCloseGig?: (gigId: string) => void;
  onPublishGig?: (gigId: string) => void;
  onDeleteGig?: (gigId: string) => void;
  onViewInterests?: (gigId: string) => void;
}

export function GigDetailModal({ 
  gig, 
  hasInterest, 
  isOwnGig, 
  onClose, 
  onShowInterest, 
  onViewProfile,
  onEdit,
  onCloseGig,
  onPublishGig,
  onDeleteGig,
  onViewInterests
}: GigDetailModalProps) {
  useModalFocus(true, { fullscreen: true }); // Full-screen modal: hide header/footer while open
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState<'closing' | 'publishing' | 'deleting' | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Browser history for back button
  useEffect(() => {
    const modalUrl = new URL(window.location.href);
    modalUrl.searchParams.set('view', 'gigs');
    if (gig?.id) {
      modalUrl.searchParams.set('gigId', gig.id);
    }
    window.history.pushState({ modal: 'gig-detail', gigId: gig?.id, view: 'gigs' }, '', `${modalUrl.pathname}${modalUrl.search}`);
    
    const handlePopState = (e: PopStateEvent) => {
      // If history moved TO this modal state (nested modal closed), keep this modal open.
      if (e.state?.modal === 'gig-detail') return;
      onCloseRef.current();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [gig?.id]);

  const handlePosterClick = () => {
    // Robust against partial gig payloads where poster can be missing.
    const employerId = gig?.poster?.id || gig?.employerId;
    if (!employerId) return;
    if (gig?.id) {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('view', 'gigs');
      returnUrl.searchParams.set('gigId', gig.id);
      window.history.pushState(
        { view: 'gigs', reopenGigId: gig.id, fromView: 'profile' },
        '',
        `${returnUrl.pathname}${returnUrl.search}`
      );
      sessionStorage.setItem(
        'talentfunn_pending_gig_profile_return',
        JSON.stringify({ gigId: gig.id, at: Date.now() })
      );
    }
    onClose();
    onViewProfile(employerId);
  };

  const handleShowInterest = () => {
    onShowInterest(gig.id);
  };

  const handleEdit = () => {
    if (onEdit) onEdit(gig.id);
  };

  const handleCloseGig = () => {
    if (onCloseGig) {
      setActionLoading('closing');
      onCloseGig(gig.id);
      setTimeout(() => setActionLoading(null), 3000);
    }
  };

  const handlePublishGig = () => {
    if (onPublishGig) {
      setActionLoading('publishing');
      onPublishGig(gig.id);
      setTimeout(() => setActionLoading(null), 3000);
    }
  };

  const handleDeleteGig = () => {
    if (onDeleteGig) {
      setActionLoading('deleting');
      onDeleteGig(gig.id);
      setShowDeleteConfirm(false);
      setTimeout(() => setActionLoading(null), 3000);
    }
  };

  const handleViewInterests = () => {
    if (onViewInterests) onViewInterests(gig.id);
  };
  const gigImageUrl = gig?.imageUrl || gig?.image_url || gig?.image;

  return (
    <FullscreenShell
      title="Oppdragsdetaljer"
      onClose={onClose}
      onBack={onClose}
      overlayClassName="gig-modal-overlay gig-modal-overlay-detail"
      onDesktopBackdropClick={onClose}
      compactSpacer
      overlayMotionProps={{
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 0.18, ease: 'easeOut' },
      }}
      contentClassName="px-0 py-0"
    >
      <div className="relative">
        <div className="edit-profile-drawer-content px-6 py-0 space-y-6 pb-20">
          {/* Poster info */}
          <div 
            className="flex items-center gap-4 cursor-pointer hover:bg-purple-50 -mx-2 p-2 rounded-lg transition-colors"
            onClick={handlePosterClick}
          >
            <div className="relative">
              {gig.poster.profileImage ? (
                <img
                  src={gig.poster.profileImage}
                  alt={gig.poster.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                  <Briefcase className="w-8 h-8 text-white" />
                </div>
              )}
              {/* Arbeidsgiver Pluss badge */}
              {canCreateGig(gig.poster) && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center border-2 border-white">
                  <Crown className="w-3.5 h-3.5 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-purple-900">{gig.poster.name}</h3>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                {canCreateGig(gig.poster) ? (
                  <>
                    <Crown className="w-3 h-3 text-orange-500" />
                    Arbeidsgiver Pluss
                  </>
                ) : (
                  <>
                    <Briefcase className="w-3 h-3 text-gray-500" />
                    Arbeidsgiver
                  </>
                )}
              </p>
              {gig.poster.county && (
                <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  {gig.poster.county}
                </p>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <h1 className="text-2xl font-bold text-purple-900 mb-2">{gig.title}</h1>
            {gig.status === 'closed' && (
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                <XCircle className="w-4 h-4" />
                Ikke publisert
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <h4 className="font-semibold text-purple-900 mb-2">Beskrivelse</h4>
            <LinkifiedText text={gig.description} className="text-gray-700" />
          </div>

          {/* Image */}
          {gigImageUrl && (
            <div>
              <img
                src={gigImageUrl}
                alt={gig.title}
                className="w-full rounded-lg object-cover"
              />
            </div>
          )}

          {/* Skills */}
          {gig.skills && gig.skills.length > 0 && (
            <div>
              <h4 className="font-semibold text-purple-900 mb-2">Ønskede ferdigheter</h4>
              <div className="flex flex-wrap gap-2">
                {gig.skills.map((skill: string, index: number) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Meta info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {gig.county && (
              <div>
                <h4 className="font-semibold text-purple-900 mb-1 text-sm">Sted</h4>
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="w-4 h-4" />
                  <span>{gig.county}</span>
                </div>
              </div>
            )}
            {gig.budget && (
              <div>
                <h4 className="font-semibold text-purple-900 mb-1 text-sm">Budsjett</h4>
                <div className="flex items-center gap-2 text-gray-700">
                  <Briefcase className="w-4 h-4" />
                  <span>{gig.budget}</span>
                </div>
              </div>
            )}
            {gig.deadline && (
              <div>
                <h4 className="font-semibold text-purple-900 mb-1 text-sm">Frist</h4>
                <div className="text-gray-700">
                  {new Date(gig.deadline).toLocaleDateString('nb-NO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            )}
            {gig.viewsCount !== undefined && (
              <div>
                <h4 className="font-semibold text-purple-900 mb-1 text-sm">Visninger</h4>
                <div className="flex items-center gap-2 text-gray-700">
                  <Eye className="w-4 h-4" />
                  <span>{gig.viewsCount}</span>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="border-t border-gray-200 pt-6 space-y-3 xl:pb-8">
            {isOwnGig ? (
              <>
                {/* Own gig actions */}
                <button
                  onClick={handlePosterClick}
                  className="w-full px-6 py-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-semibold"
                >
                  Se min profil
                </button>
                {onEdit && gig.status === 'active' && (
                  <button
                    onClick={handleEdit}
                    className="w-full px-6 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Rediger oppdrag
                  </button>
                )}
                {onCloseGig && gig.status === 'active' && (
                  <button
                    onClick={handleCloseGig}
                    disabled={!!actionLoading}
                    className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle className="w-4 h-4" />
                    {actionLoading === 'closing' ? 'Avpubliserer...' : 'Avpubliser'}
                  </button>
                )}
                {onPublishGig && gig.status === 'closed' && (
                  <button
                    onClick={handlePublishGig}
                    disabled={!!actionLoading}
                    className="w-full px-6 py-3 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload className="w-4 h-4" />
                    {actionLoading === 'publishing' ? 'Publiserer...' : 'Publiser igjen'}
                  </button>
                )}
                {onDeleteGig && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full px-6 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Slett oppdrag
                  </button>
                )}
                {onViewInterests && (
                  <button
                    onClick={handleViewInterests}
                    className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Interesserte
                  </button>
                )}
              </>
            ) : (
              <>
                {/* Other user's gig actions */}
                <button
                  onClick={handlePosterClick}
                  className="w-full px-6 py-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-semibold"
                >
                  Se arbeidsgivers profil
                </button>
                {gig.status === 'active' && (
                  hasInterest ? (
                    <button
                      disabled
                      className="w-full px-6 py-3 bg-green-100 text-green-700 rounded-lg cursor-not-allowed font-semibold flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Du har vist interesse for dette oppdraget
                    </button>
                  ) : (
                    <button
                      onClick={handleShowInterest}
                      className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
                    >
                      Vis interesse for oppdraget
                    </button>
                  )
                )}
              </>
            )}
        </div>
        </div>

        {/* Bekreft sletting */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center p-4 z-20">
            <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Slett oppdrag?</h3>
              <p className="text-gray-600 text-sm mb-6">
                Dette kan ikke angres. Oppdraget og alle tilknyttede henvendelser slettes permanent.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleDeleteGig}
                  disabled={!!actionLoading}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === 'deleting' ? 'Sletter...' : 'Slett'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </FullscreenShell>
  );
}
